import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { api } from '../utils/api';

const formatEuro = (value) => `€ ${(parseFloat(value) || 0).toFixed(2)}`;

const EditTodayPaymentsDialog = ({ open, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [movements, setMovements] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);

  const todayStr = format(new Date(), 'yyyy-MM-dd');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [movRes, pmRes, accRes] = await Promise.all([
        api.get('/accounting-movements', { params: { from: todayStr, to: todayStr } }),
        api.get('/payment-methods'),
        api.get('/accounts')
      ]);
      const methods = pmRes.data || [];
      const accs = accRes.data || [];
      setPaymentMethods(methods);
      setAccounts(accs);
      setMovements(movRes.data || []);
    } catch (err) {
      setError(err.response?.data?.msg || 'Errore nel caricamento movimenti');
    } finally {
      setLoading(false);
    }
  }, [todayStr]);

  useEffect(() => {
    if (open) {
      setSelected(null);
      setForm(null);
      loadData();
    }
  }, [open, loadData]);

  const openEdit = (movement) => {
    setSelected(movement);
    setForm({
      description: movement.description,
      amount: String(movement.amount),
      date: parseISO(movement.date),
      type: movement.type,
      paymentMethodId: movement.paymentMethodId,
      accountId: movement.accountId
    });
    setError('');
  };

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    if (!selected || !form) return;
    const amount = parseFloat(String(form.amount).replace(',', '.'));
    if (!form.description.trim()) {
      setError('La descrizione è obbligatoria');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Inserisci un importo valido');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.put(`/accounting-movements/${selected.id}`, {
        description: form.description.trim(),
        amount,
        date: format(form.date, 'yyyy-MM-dd'),
        type: form.type,
        paymentMethodId: form.paymentMethodId,
        accountId: form.accountId
      });
      if (onSaved) onSaved();
      setSelected(null);
      setForm(null);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.msg || err.response?.data?.errors?.[0]?.msg || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const getMethodName = (id) => paymentMethods.find((m) => m.id === id)?.name || '-';
  const getAccountName = (id) => accounts.find((a) => a.id === id)?.name || '-';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Modifica Pagamento</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !selected ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Movimenti contabili del {format(new Date(), 'dd/MM/yyyy', { locale: it })}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Ora</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Descrizione</TableCell>
                  <TableCell>Metodo</TableCell>
                  <TableCell align="right">Importo</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.map((movement) => (
                  <TableRow key={movement.id} hover>
                    <TableCell>
                      {movement.createdAt
                        ? format(new Date(movement.createdAt), 'HH:mm')
                        : '-'}
                    </TableCell>
                    <TableCell>{movement.type === 'IN' ? 'Entrata' : 'Uscita'}</TableCell>
                    <TableCell>
                      <Box>
                        {movement.description}
                        {movement.modificationNote && (
                          <Typography
                            variant="caption"
                            component="div"
                            color="warning.main"
                            sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                          >
                            {movement.modificationNote}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{movement.PaymentMethod?.name || getMethodName(movement.paymentMethodId)}</TableCell>
                    <TableCell align="right">{formatEuro(movement.amount)}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => openEdit(movement)}>Modifica</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!movements.length && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">Nessun movimento contabile oggi</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        ) : (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Button size="small" onClick={() => { setSelected(null); setForm(null); }}>
                ← Torna all&apos;elenco
              </Button>
            </Grid>
            {selected.modificationNote && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Storico modifiche
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                    {selected.modificationNote}
                  </Typography>
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label="Descrizione"
                fullWidth
                value={form.description}
                onChange={handleChange('description')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Importo"
                type="number"
                fullWidth
                value={form.amount}
                onChange={handleChange('amount')}
                inputProps={{ min: 0.01, step: '0.01' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select label="Tipo" value={form.type} onChange={handleChange('type')}>
                  <MenuItem value="IN">Entrata</MenuItem>
                  <MenuItem value="OUT">Uscita</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
                <DatePicker
                  label="Data"
                  value={form.date}
                  onChange={(date) => setForm((prev) => ({ ...prev, date: date || new Date() }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Metodo di pagamento</InputLabel>
                <Select
                  label="Metodo di pagamento"
                  value={form.paymentMethodId}
                  onChange={handleChange('paymentMethodId')}
                >
                  {paymentMethods.map((method) => (
                    <MenuItem key={method.id} value={method.id}>{method.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Conto</InputLabel>
                <Select label="Conto" value={form.accountId} onChange={handleChange('accountId')}>
                  {accounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>{account.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                {getMethodName(form.paymentMethodId)} · {getAccountName(form.accountId)}
              </Typography>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Chiudi</Button>
        {selected && (
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvataggio...' : 'Salva modifiche'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EditTodayPaymentsDialog;
